import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Pct, Spark } from "@/components/layernav/primitives";
import { fmtBig, sectorTrack } from "./SectorHero";
import { ProtocolLogo } from "./ProtocolLogo";
import { entityNavRoute, entityTrack, resolveEntity, useEntityRegistry } from "@/lib/entities";

const ROW = "grid grid-cols-[20px_minmax(0,1.6fr)_72px_48px_66px] md:grid-cols-[28px_minmax(0,1.2fr)_96px_58px_58px_110px_minmax(0,0.9fr)_88px] items-center gap-x-2 md:gap-x-3";

const TokenMove = ({ token }: { token: any }) => {
  if (!token) return <span className="font-mono text-[10px] text-gray-600 text-right block">—</span>;
  const inner = (
    <span className="flex items-center justify-end gap-1.5">
      <span className="font-mono text-[10px] font-bold text-[#A78BFA]">{token.symbol}</span>
      <Pct v={token.change_24h_pct} className="!text-[10.5px]" />
    </span>
  );
  return token.has_coin_page ? (
    <Link to={`/coin/${token.symbol}`} title={`${token.symbol} $${token.price_usd < 10 ? token.price_usd.toFixed(3) : token.price_usd.toLocaleString()}`}
      data-testid={`activity-token-${token.symbol}`}
      onClick={(e) => { e.stopPropagation(); sectorTrack("market_module_interaction", `token_${token.symbol}`, "defi"); }}
      className="relative z-10 rounded hover:bg-white/[0.06] px-1 -mx-1 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#A78BFA]/60">{inner}</Link>
  ) : <span data-testid={`activity-token-${token.symbol}`}>{inner}</span>;
};

/* nav: Entity Discovery UX row treatment (founder-fixture gated — enable
   per surface only after visual approval). Live registry entities become
   one stretched clickable target; non-live rows stay inert (Rule 6). */
export const ProtocolRow = ({ p, testPrefix = "activity", nav = true }: {
  p: any; testPrefix?: string; nav?: boolean;
}) => {
  const { data: reg } = useEntityRegistry();
  const route = nav ? entityNavRoute(resolveEntity(reg, "protocol", p.slug)) : null;
  return (
  <div data-testid={`${testPrefix}-row-${p.slug}`} data-live={nav ? (route ? "true" : "false") : undefined}
    className={`${ROW} ${route ? "group relative" : ""} rounded-lg px-2 md:px-3 py-2.5 -mx-2 md:-mx-3 hover:bg-white/[0.04] transition-colors`}>
    <span className="font-mono text-[10px] text-gray-500">{p.rank}</span>
    <span className="min-w-0 flex items-center gap-2">
      <ProtocolLogo slug={p.slug} name={p.protocol} size={26}
        className={route ? "transition-transform duration-200 group-hover:scale-[1.05]" : ""} />
      {route ? (
        <Link to={route} aria-label={`${p.protocol} — Entity Intelligence`}
          data-testid={`entity-link-protocol-${p.slug}`}
          onClick={() => entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: testPrefix })}
          className="min-w-0 focus-visible:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-1 focus-visible:after:ring-[#00E5FF]/60">
          <span className="flex items-center gap-1 min-w-0">
            <span className="font-bold text-[13.5px] text-white leading-tight truncate transition-colors duration-200 group-hover:text-[#00E5FF]">{p.protocol}</span>
            <ArrowUpRight aria-hidden
              className="h-3 w-3 shrink-0 text-[#00E5FF] opacity-50 md:opacity-0 transition-opacity duration-200 md:group-hover:opacity-100 md:group-focus-within:opacity-100" />
          </span>
          <span className="block font-mono text-[9px] uppercase tracking-wide text-gray-400 truncate">{p.category}</span>
        </Link>
      ) : (
        <span className="min-w-0">
          <span className="block font-bold text-[13.5px] text-white leading-tight truncate">{p.protocol}</span>
          <span className="block font-mono text-[9px] uppercase tracking-wide text-gray-400 truncate">{p.category}</span>
        </span>
      )}
    </span>
    <span className="font-mono text-[13px] font-semibold text-white text-right">{fmtBig(p.tvl_usd)}</span>
    <span className="text-right">
      {p.tvl_change_24h_pct != null ? <Pct v={p.tvl_change_24h_pct} className="!text-[11px]" />
        : <span className="font-mono text-[10px] text-gray-600">—</span>}
    </span>
    <span className="text-right hidden md:block">
      {p.tvl_change_7d_pct != null ? <Pct v={p.tvl_change_7d_pct} className="!text-[11px]" />
        : <span className="font-mono text-[10px] text-gray-600">—</span>}
    </span>
    <span className="hidden md:flex justify-center" title="7-day TVL trend · NewsLayer series">
      {p.spark?.length >= 2
        ? <Spark points={p.spark} up={p.spark[p.spark.length - 1] >= p.spark[0]} className="w-[84px] h-6" />
        : <span className="font-mono text-[9px] text-gray-600">building…</span>}
    </span>
    <span className="hidden md:block font-mono text-[10px] text-gray-400 truncate">
      {p.dominant_chain}{p.chains?.length > 1 ? ` +${p.chains.length - 1}` : ""}
    </span>
    <span className="text-right md:text-right">
      <span className="md:hidden" title="7-day TVL trend">
        {p.spark?.length >= 2
          ? <Spark points={p.spark} up={p.spark[p.spark.length - 1] >= p.spark[0]} className="w-[60px] h-5" />
          : <span className="font-mono text-[9px] text-gray-600">—</span>}
      </span>
      <span className="hidden md:block"><TokenMove token={p.token} /></span>
    </span>
  </div>
  );
};

export const ProtocolActivity = ({ data }: { data: any }) => {
  const protocols: any[] = data?.protocols || [];
  if (!protocols.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid="defi-protocol-activity">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="font-heading font-bold text-white text-lg">
          Protocol Activity
          <span className="ml-3 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#34D399] align-middle">Live TVL</span>
        </h2>
        <Link to="/defi/protocols" onClick={() => sectorTrack("section_view_all", "protocol_activity", "defi")}
          className="group font-mono text-[11px] font-bold text-[#34D399] hover:text-white transition-colors whitespace-nowrap">
          All protocols <ArrowRight className="inline h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
      <div className="rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 md:p-5">
        <div className={`${ROW} font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1 px-2 md:px-3 -mx-2 md:-mx-3`}>
          <span>#</span><span>Protocol</span><span className="text-right">TVL</span>
          <span className="text-right">24H</span>
          <span className="text-right hidden md:block">7D</span>
          <span className="text-center hidden md:block">7D Trend</span>
          <span className="hidden md:block">Chains</span>
          <span className="text-right"><span className="md:hidden">7D Trend</span><span className="hidden md:inline">Token 24H</span></span>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {protocols.map((p) => <ProtocolRow key={p.slug} p={p} />)}
        </div>
        <p className="mt-3 font-mono text-[8.5px] uppercase tracking-wider text-gray-500" title={data?.eligibility?.rule}>
          TVL & changes · DeFiLlama · 7D trend from NewsLayer-owned series · Token prices via Kraken
        </p>
      </div>
    </section>
  );
};
